import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, filter, map, of } from 'rxjs';
import { ApiRouter } from '../../../../../global/routes/api-routes';
import { LookupResponse, Option } from '../../../../../shared/interfaces/lookup-options.interfaces';
import { ClaimsFormService } from './claims-form.service';

@Injectable({
  providedIn: 'root',
})
export class LookupOptionsService {
  cachedResponses: Record<string, Record<string, any[]>> = {};

  loadedValuesMappedIntoObject = signal<Record<string, Record<string, Option>>>({});

  constructor(
    private readonly http: HttpClient,
    private readonly claimFormService: ClaimsFormService,
    private readonly injector: Injector
  ) {
    this.listenToClaimStateUpdatesToAllowPreLoadingData();
  }

  listenToClaimStateUpdatesToAllowPreLoadingData(): void {
    if (!this.injector) return;
    toObservable(this.claimFormService.currentClaimState, { injector: this.injector })
      .pipe(filter((v) => !!v))
      .subscribe({
        next: (value) => {
          value?.steps.forEach((step) => {
            Object.values(step.stepConfig).forEach((config) => {
              if (!config.dynamicOptions) return;
              this.lookupOptionsForStep(config.name, '').subscribe();
            });
          });
        },
      });
  }

  lookupOptionsForStep(step: string, query: string): Observable<Option[]> {
    // if (query === '') return of([]);
    const { planNumber, claimId } = this.claimFormService.state;
    const allStepConfigs = this.claimFormService.state.currentClaimSteps!.stepConfig;
    const currentStepConfig = allStepConfigs[step];

    const dependencyMap = (currentStepConfig.dynamicOptions?.dependencies ?? []).reduce(
      (a: Record<string, string>, b: string) => {
        return {
          ...a,
          [b]: allStepConfigs[b].value,
        };
      },
      {}
    );

    const url = ApiRouter.lookupOptions(planNumber, claimId as string, step);
    const body = { query, dependencies: dependencyMap };

    if (this.cachedResponses[step]) {
      if (this.cachedResponses[step][query]) {
        return of(this.cachedResponses[step][query]);
      }
    }

    return this.http.post<LookupResponse>(url, body).pipe(
      map((value) => {
        if (!this.cachedResponses[step]) {
          this.cachedResponses[step] = {};
        }

        let mappedValue = value.map((option) => {
          return option.options.map((i) => {
            return { ...i, title: i.title, group: option.title };
          });
        });

        this.cachedResponses[step][query] = mappedValue.flat();

        const optionsArray = mappedValue.flat();

        const existingLoadedInValues = this.loadedValuesMappedIntoObject()[step] ?? {};

        const newLoadedInValues = optionsArray.reduce((a, b) => {
          a[b.value] = b;
          return a;
        }, existingLoadedInValues);

        this.loadedValuesMappedIntoObject.update((state) => {
          return {
            ...state,
            [step]: newLoadedInValues,
          };
        });

        return optionsArray;
      })
    );
  }
}
